import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import {
  createBrowserRouter,
  redirect,
  RouterProvider,
} from "react-router-dom";

import { Center, Image, Spinner, Text, useToast, VStack } from "@chakra-ui/react";

import Layout from "./Layout/Layout";
import Home from "./Pages/Home";
import PageNotFound from "./Pages/PageNotFound";
import { getAuthToken, updateUserCache, removeUserAndToken } from "./utilities/auth";
import Fallback from "./Pages/Fallback";
import AllGames from "./Pages/AllGames";
import Privacy from "./Pages/Privacy";
import Terms from "./Pages/TermsofUse";
import Comics from "./Pages/Comics";
import Trivia from "./Pages/Trivia";
import AllNews from "./Pages/News";
import NewsPage from "./Pages/NewsPage";
import leaderboredLogo from "./assets/Leaderbored_logo_HD.webp";

const GamePage = lazy(() => import("./Pages/GamePage"));
const SearchResult = lazy(() => import("./Pages/SearchResult"));
const AuthPage = lazy(() => import("./Pages/AuthPage"));
const Account = lazy(() => import("./Pages/Account"));
const ResetPasswordPage = lazy(() => import("./Pages/ResetPasswordPage"));
const Contact = lazy(() => import("./Pages/ContactUs"));
const About = lazy(() => import("./Pages/About"));
const CategoryPage = lazy(() => import("./Pages/CategoryPage"));

const router = createBrowserRouter([
  {
    path: "/",
    id: "root",
    element: <Layout />,
    errorElement: <PageNotFound />,
    children: [
      {
        index: true,
        element: <Home />,
      },
      {
        path: "home",
        element: <Home />,
      },
      {
        path: "about",
        element: (
          <Suspense fallback={<Fallback />}>
            <About />,
          </Suspense>
        ),
      },
      {
        path: "games/:slugId",
        element: (
          <Suspense fallback={<Fallback />}>
            <GamePage />
          </Suspense>
        ),
      },
      {
        path: "search",
        element: (
          <Suspense fallback={<Fallback />}>
            <SearchResult />
          </Suspense>
        ),
      },
      {
        path: "auth",
        element: (
          <Suspense fallback={<Fallback />}>
            <AuthPage />
          </Suspense>
        ),
        loader: () =>
          import("./utilities/auth").then((module) => module.checkAuthLoader()),
      },
      {
        path: "auth/reset",
        element: (
          <Suspense fallback={<Fallback />}>
            <ResetPasswordPage />
          </Suspense>
        ),
      },
      {
        path: "account",
        element: (
          <Suspense fallback={<Fallback />}>
            <Account />
          </Suspense>
        ),
        loader: () =>
          import("./utilities/auth").then((module) => {
            const token = module.getAuthToken();
            if (token) return null;
            else return redirect("/");
          }),
      },
      {
        path: "contact",
        element: (
          <Suspense fallback={Fallback}>
            <Contact />
          </Suspense>
        ),
      },
      {
        path: "games",
        element: (
          <Suspense fallback={Fallback}>
            <AllGames />
          </Suspense>
        )
      },
      {
        path: "privacypolicy",
        element: (
          <Suspense fallback={Fallback}>
            <Privacy />
          </Suspense>
        )
      },
      {
        path: "termsofuse",
        element: (
          <Suspense fallback={Fallback}>
            <Terms />
          </Suspense>
        )
      },
      {
        path: "comics",
        element: (
          <Suspense fallback={Fallback}>
            <Comics />
          </Suspense>
        )
      },
      {
        path: "trivia",
        element: (
          <Suspense fallback={Fallback}>
            <Trivia />
          </Suspense>
        )
      },
      {
        path: "/g/:category",
        element: (
          <Suspense fallback={Fallback}>
            <CategoryPage />
          </Suspense>)
      },
      {
        path: "news",
        element: (
          <Suspense fallback={Fallback}>
            <AllNews />
          </Suspense>
        )
      },
      {
        path: "news/:postId",
        element: (
          <Suspense fallback={Fallback}>
            <NewsPage />
          </Suspense>
        )
      }

    ],
  },
]);

function App() {
  const toast = useToast();
  const [isReloading, setIsReloading] = useState(false);
  
  const logoutHandler = useCallback(() => {
    setIsReloading(true);
    removeUserAndToken();  
    
    toast({
        title: "Session Expired",
        description: "Logging out",
        status: "warning",
        position: "bottom",
        isClosable: true,
      });

      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }, [toast]);
  
  const token = getAuthToken();
  
  useEffect(() => { 
    if (!token) { return; }
    
    const refreshUser = async () => {
      try {
        await updateUserCache();
      } catch (error) {
        //logout the user for any 
        const status = error?.response?.status;
        if (status === 401) {
          logoutHandler();
        }
      }
    };

    refreshUser();
  }, [logoutHandler, token]);

  // this component can be added to Fallback.js
  // but explicitly not until further approval
  if (isReloading) {
    return (
      <Center minH='100vh' bg='gray.50' px='24px'>
        <VStack spacing='20px' textAlign='center' aria-live='polite'>
          <Image
            src={leaderboredLogo}
            alt='Leaderbored'
            w={{ base: "140px", md: "180px" }}
          />
          <Spinner
            thickness='4px'
            speed='0.65s'
            emptyColor='secondary'
            color='primary'
            size='xl'
          />
          <Text color='primaryDark' fontSize='lg' fontWeight='600'>
            Redirecting...
          </Text>
        </VStack>
      </Center>
    );
  }

  return <RouterProvider router={router} />;
}

export default App;
